import * as React from 'react';
import { Listbox, Combobox, Transition } from '@headlessui/react';
import { PatternFormat, NumericFormat } from 'react-number-format';
import { usePlacesWidget } from 'react-google-autocomplete';
import { usaStates } from 'typed-usa-states/dist/states';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
  __assign = Object.assign || function __assign(t) {
      for (var s, i = 1, n = arguments.length; i < n; i++) {
          s = arguments[i];
          for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
      return t;
  };
  return __assign.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
      t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function")
      for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
          if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
              t[p[i]] = s[p[i]];
      }
  return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var classnames = {exports: {}};

/*!
	Copyright (c) 2018 Jed Watson.
	Licensed under the MIT License (MIT), see
	http://jedwatson.github.io/classnames
*/

(function (module) {
/* global define */

(function () {

	var hasOwn = {}.hasOwnProperty;

	function classNames() {
		var classes = [];

		for (var i = 0; i < arguments.length; i++) {
			var arg = arguments[i];
			if (!arg) continue;

			var argType = typeof arg;

			if (argType === 'string' || argType === 'number') {
				classes.push(arg);
			} else if (Array.isArray(arg)) {
				if (arg.length) {
					var inner = classNames.apply(null, arg);
					if (inner) {
						classes.push(inner);
					}
				}
			} else if (argType === 'object') {
				if (arg.toString !== Object.prototype.toString && !arg.toString.toString().includes('[native code]')) {
					classes.push(arg.toString());
					continue;
				}

				for (var key in arg) {
					if (hasOwn.call(arg, key) && arg[key]) {
						classes.push(key);
					}
				}
			}
		}

		return classes.join(' ');
	}

	if (module.exports) {
		classNames.default = classNames;
		module.exports = classNames;
	} else {
		window.classNames = classNames;
	}
}());
}(classnames));

var classNames = classnames.exports;

function getButtonClassNames(_a) {
    var _b = _a.size, size = _b === void 0 ? "default" : _b, _c = _a.variant, variant = _c === void 0 ? "filled" : _c, disabled = _a.disabled;
    return classNames("savvy-ui-flex savvy-ui-items-center savvy-ui-justify-center savvy-ui-leading-none", {
        "savvy-ui-font-bold savvy-ui-uppercase": variant !== "text",
        "savvy-ui-font-semibold": variant === "text",
        "savvy-ui-h-12 savvy-ui-w-full savvy-ui-rounded-[48px]": size === "default" && variant !== "text",
        "savvy-ui-p-2 savvy-ui-rounded savvy-ui-text-sm": size === "small",
        "savvy-ui-text-white": variant === "filled",
        "savvy-ui-bg-savvy savvy-ui-transition-colors hover:savvy-ui-bg-[#1C5AC9] focus:savvy-ui-bg-[#1C5AC9]": variant === "filled" && !disabled,
        "savvy-ui-bg-savvy-200": variant === "filled" && disabled,
        "savvy-ui-border-current": variant === "outlined",
        "savvy-ui-border-[2px] ": variant === "outlined" && size === "default",
        "savvy-ui-border": variant === "outlined" && size === "small",
        "savvy-ui-text-savvy savvy-ui-transition-colors hover:savvy-text-[#1C5AC9] focus:savvy-ui-text-[#1C5AC9]": ["outlined", "text"].includes(variant) && !disabled,
        "savvy-ui-text-savvy-200": ["outlined", "text"].includes(variant) && disabled,
    });
}
var WrappedButton = React.forwardRef(function Button(_a, ref) {
    var className = _a.className, _b = _a.size, size = _b === void 0 ? "default" : _b, _c = _a.variant, variant = _c === void 0 ? "filled" : _c, rest = __rest(_a, ["className", "size", "variant"]);
    return (React.createElement("button", __assign({ className: classNames(getButtonClassNames({
            size: size,
            variant: variant,
            disabled: rest.disabled,
        }), className) }, rest, { ref: ref })));
});

function getTextFieldInputClasses(_a) {
    var className = _a.className, hasError = _a.hasError;
    return classNames("savvy-ui-p-4 savvy-ui-text-base savvy-ui-leading-4 savvy-ui-w-full savvy-ui-appearance-none savvy-ui-ring-1 savvy-ui-rounded savvy-ui-h-full focus:savvy-ui-outline-none placeholder:savvy-ui-text-gray-200", className, {
        "savvy-ui-ring-gray-200 focus-within:savvy-ui-ring-savvy": !hasError,
        "savvy-ui-ring-red": hasError,
    });
}
var labelClasses = "savvy-ui-text-xs savvy-ui-leading-3 savvy-ui-font-semibold savvy-ui-absolute -savvy-ui-top-3 savvy-ui-translate-y-1.5 savvy-ui-left-3 savvy-ui-px-1 savvy-ui-bg-white savvy-ui-z-10";

function TextFieldLabel(_a) {
    var children = _a.children, className = _a.className, rest = __rest(_a, ["children", "className"]);
    var labelClassNames = classNames(labelClasses, className);
    return typeof children === "string" ? (React.createElement("label", __assign({ className: labelClassNames, dangerouslySetInnerHTML: { __html: children } }, rest))) : (React.createElement("label", __assign({ className: labelClassNames }, rest), children));
}
function TextFieldContainer(_a) {
    var children = _a.children, label = _a.label, labelClassName = _a.labelClassName, className = _a.className, id = _a.id, errorMessage = _a.errorMessage, rest = __rest(_a, ["children", "label", "labelClassName", "className", "id", "errorMessage"]);
    return (React.createElement("div", __assign({ className: classNames("savvy-ui-group savvy-ui-relative savvy-ui-flex savvy-ui-flex-col savvy-ui-justify-center savvy-ui-rounded savvy-ui-bg-white", className) }, rest),
        label ? (React.createElement(TextFieldLabel, { htmlFor: id, className: classNames(labelClassName, {
                "savvy-ui-text-gray-100 group-focus-within:savvy-ui-text-savvy": !rest["aria-invalid"],
                "savvy-ui-text-red": rest["aria-invalid"],
            }) }, label)) : null,
        children,
        rest["aria-invalid"] && errorMessage ? (React.createElement("p", { className: "savvy-ui-mt-1 savvy-ui-text-xs savvy-ui-font-bold savvy-ui-text-red" }, errorMessage)) : null));
}

function TextFieldPrefix(props) {
    return (React.createElement("div", __assign({ className: "savvy-ui-pointer-events-none savvy-ui-absolute savvy-ui-top-0 savvy-ui-bottom-0 savvy-ui-flex savvy-ui-items-center savvy-ui-justify-center" }, props)));
}

var WrappedTextField$1 = React.forwardRef(function TextField(_a, ref) {
    var className = _a.className, style = _a.style, label = _a.label, labelClassName = _a.labelClassName, name = _a.name, id = _a.id, wrapperClassName = _a.wrapperClassName, wrapperStyle = _a.wrapperStyle, errorMessage = _a.errorMessage, inputPrefix = _a.inputPrefix, _b = _a.prefixSpacing, prefixSpacing = _b === void 0 ? 32 : _b, rest = __rest(_a, ["className", "style", "label", "labelClassName", "name", "id", "wrapperClassName", "wrapperStyle", "errorMessage", "inputPrefix", "prefixSpacing"]);
    return (React.createElement(TextFieldContainer, { className: wrapperClassName, label: label, labelClassName: labelClassName, id: id || name, style: wrapperStyle, "aria-invalid": rest["aria-invalid"], errorMessage: errorMessage },
        React.createElement("div", { className: "savvy-ui-relative" },
            React.createElement("input", __assign({ className: getTextFieldInputClasses({
                    className: className,
                    hasError: Boolean(rest["aria-invalid"]),
                }), ref: ref, name: name, id: id || name, style: __assign(__assign({}, style), (Boolean(inputPrefix) && { paddingLeft: prefixSpacing })) }, rest)),
            inputPrefix ? (React.createElement(TextFieldPrefix, { style: { width: prefixSpacing } }, inputPrefix)) : null)));
});

function CheckboxLabel(_a) {
    var className = _a.className, centerLabel = _a.centerLabel, children = _a.children, rest = __rest(_a, ["className", "centerLabel", "children"]);
    var labelClasses = classNames("savvy-ui-ml-3 savvy-ui-text-black-100 savvy-ui-text-left", className, {
        "savvy-ui-leading-[1.2]": !centerLabel,
        "savvy-ui-leading-6": centerLabel,
    });
    return typeof children === "string" ? (React.createElement("span", __assign({ className: labelClasses }, rest, { dangerouslySetInnerHTML: { __html: children } }))) : (React.createElement("span", __assign({ className: labelClasses }, rest), children));
}
var WrappedCheckbox = React.forwardRef(function Checkbox(_a, ref) {
    var className = _a.className, label = _a.label, _b = _a.centerLabel, centerLabel = _b === void 0 ? false : _b, _c = _a.contained, contained = _c === void 0 ? false : _c, labelClassName = _a.labelClassName, wrapperClassName = _a.wrapperClassName, rest = __rest(_a, ["className", "label", "centerLabel", "contained", "labelClassName", "wrapperClassName"]);
    return (React.createElement("label", { htmlFor: rest.id, className: classNames("savvy-ui-flex", wrapperClassName, {
            "savvy-ui-items-center": centerLabel,
            "savvy-ui-items-start": !centerLabel,
        }, {
            "savvy-ui-cursor-pointer savvy-ui-items-center savvy-ui-rounded-lg savvy-ui-border savvy-ui-border-gray-200 savvy-ui-p-4": contained,
        }) },
        React.createElement("div", { className: "savvy-ui-relative savvy-ui-inline-flex savvy-ui-flex-none" },
            React.createElement("input", __assign({ type: "checkbox", className: classNames("savvy-ui-peer savvy-ui-h-6 savvy-ui-w-6 savvy-ui-appearance-none savvy-ui-rounded savvy-ui-border savvy-ui-leading-6 checked:savvy-ui-border-savvy checked:savvy-ui-bg-savvy hover:savvy-ui-cursor-pointer hover:savvy-ui-border-savvy hover:savvy-ui-bg-gray-300 checked:hover:savvy-ui-bg-savvy-100 focus:savvy-ui-outline focus:savvy-ui-outline-2 focus:savvy-ui-outline-offset-0 focus:savvy-ui-outline-savvy-200", {
                    "savvy-ui-border-gray-200 savvy-ui-bg-gray-300 checked:savvy-ui-border-gray-200 checked:savvy-ui-bg-gray-300 hover:savvy-ui-border-gray-200": rest.disabled,
                }, className), ref: ref }, rest)),
            React.createElement("span", { className: classNames("savvy-ui-pointer-events-none savvy-ui-invisible savvy-ui-absolute savvy-ui-inset-0 savvy-ui-flex savvy-ui-items-center savvy-ui-justify-center peer-checked:savvy-ui-visible", {
                    "savvy-ui-text-black-100": !rest.disabled,
                    "savvy-ui-text-[#AEB0B1]": rest.disabled,
                }) },
                React.createElement("svg", { width: "14", height: "12", viewBox: "0 0 14 12", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                    React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M13.7045 0.153466C14.034 0.404497 14.0976 0.875094 13.8466 1.20457L5.84657 11.7046C5.71541 11.8767 5.51627 11.9838 5.30033 11.9983C5.08439 12.0129 4.87271 11.9334 4.71967 11.7804L0.21967 7.28037C-0.0732233 6.98748 -0.0732233 6.5126 0.21967 6.21971C0.512563 5.92682 0.987437 5.92682 1.28033 6.21971L5.17351 10.1129L12.6534 0.295507C12.9045 -0.0339712 13.3751 -0.0975653 13.7045 0.153466Z", fill: rest.disabled ? "#CACACA" : "#FFFFFF" })))),
        label ? (React.createElement(CheckboxLabel, { centerLabel: centerLabel, className: labelClassName }, label)) : null));
});

function getDropdownClasses(_a) {
    var invalid = _a.invalid;
    return classNames("savvy-ui-absolute savvy-ui-flex savvy-ui-flex-col savvy-ui-top-full -savvy-ui-left-px savvy-ui-w-[calc(100%+2px)] savvy-ui-z-30 savvy-ui-max-h-[278px] savvy-ui-overflow-y-auto savvy-ui-overflow-x-hidden savvy-ui-rounded-b savvy-ui-border savvy-ui-bg-white savvy-ui-py-1 focus:savvy-ui-outline-none", {
        "savvy-ui-border-savvy": !invalid,
        "savvy-ui-border-red": invalid,
    });
}
function getDropdownOptionClasses(_a) {
    var active = _a.active;
    return classNames("savvy-ui-relative savvy-ui-flex savvy-ui-h-10 savvy-ui-w-full savvy-ui-shrink-0 savvy-ui-cursor-pointer savvy-ui-items-center savvy-ui-px-4 savvy-ui-text-left savvy-ui-text-black-100 hover:savvy-ui-bg-savvy hover:savvy-ui-bg-opacity-10", {
        "savvy-ui-bg-savvy savvy-ui-bg-opacity-10": active,
    });
}

var Select = React.forwardRef(function Select(_a, ref) {
    var className = _a.className, value = _a.value, onChange = _a.onChange, placeholder = _a.placeholder, options = _a.options, label = _a.label, onFocus = _a.onFocus, onBlur = _a.onBlur, _b = _a.id, id = _b === void 0 ? "" : _b, errorMessage = _a.errorMessage, rest = __rest(_a, ["className", "value", "onChange", "placeholder", "options", "label", "onFocus", "onBlur", "id", "errorMessage"]);
    var callNextFocus = React.useRef(true);
    var callNextBlur = React.useRef(false);
    function handleFocus(open) {
        if (!onFocus && !onBlur) {
            return;
        }
        if (open && callNextFocus.current) {
            if (onFocus) {
                onFocus();
            }
            callNextFocus.current = false;
            callNextBlur.current = true;
        }
        if (!open && callNextBlur.current) {
            if (onBlur) {
                onBlur();
            }
            callNextBlur.current = false;
            callNextFocus.current = true;
        }
    }
    return (React.createElement(Listbox, { value: value, onChange: onChange }, function (_a) {
        var open = _a.open;
        handleFocus(open);
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: classNames("savvy-ui-group savvy-ui-relative savvy-ui-flex savvy-ui-flex-col savvy-ui-items-start savvy-ui-justify-center savvy-ui-border", className, {
                    "savvy-ui-rounded": !open,
                    "savvy-ui-rounded-t savvy-ui-border-savvy": open,
                    "savvy-ui-border-gray-200 focus-within:savvy-ui-border-savvy": !open && !rest["aria-invalid"],
                    "savvy-ui-border-red": rest["aria-invalid"],
                }) },
                label ? (React.createElement(Listbox.Label, { className: classNames(labelClasses, {
                        "savvy-ui-text-gray-100 group-focus-within:savvy-ui-text-savvy": !open && !rest["aria-invalid"],
                        "savvy-ui-text-savvy": open,
                        "savvy-ui-text-red group-focus-within:savvy-ui-text-red": rest["aria-invalid"],
                    }) }, label)) : null,
                React.createElement(Listbox.Button, { className: "savvy-ui-relative savvy-ui-flex savvy-ui-w-full savvy-ui-items-center savvy-ui-justify-between savvy-ui-py-4 savvy-ui-px-4 savvy-ui-text-left focus:savvy-ui-outline-none", ref: ref },
                    React.createElement("span", { className: classNames("savvy-ui-flex-grow savvy-ui-truncate savvy-ui-leading-none savvy-ui-text-black-100", { "savvy-ui-text-opacity-30": !value }) }, (value === null || value === void 0 ? void 0 : value.label) || placeholder),
                    React.createElement("span", { className: classNames({
                            "savvy-ui-rotate-180": open,
                        }) },
                        React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                            React.createElement("path", { d: "M0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8Z", fill: "#CACACA" }),
                            React.createElement("rect", { x: "5.17139", y: "5.16772", width: "6.01095", height: "2", transform: "rotate(45 5.17139 5.16772)", fill: "white" }),
                            React.createElement("rect", { x: "6.63916", y: "9.35913", width: "5.9248", height: "2", transform: "rotate(-45 6.63916 9.35913)", fill: "white" })))),
                React.createElement(Listbox.Options, { className: classNames(getDropdownClasses({
                        invalid: Boolean(rest["aria-invalid"]),
                    })) }, options.map(function (option, index) {
                    return (React.createElement(Listbox.Option, { key: "".concat(id, "-").concat(option.value, "-").concat(index), value: option, as: React.Fragment }, function (_a) {
                        var active = _a.active;
                        return (React.createElement("li", { className: getDropdownOptionClasses({ active: active }) },
                            React.createElement("span", { className: "savvy-ui-truncate" }, option.label)));
                    }));
                }))),
            rest["aria-invalid"] && errorMessage ? (React.createElement("p", { className: "savvy-ui-mt-1 savvy-ui-text-xs savvy-ui-font-bold savvy-ui-text-red" }, errorMessage)) : null));
    }));
});

function RadioLabel(_a) {
    var children = _a.children, className = _a.className, rest = __rest(_a, ["children", "className"]);
    var labelClasses = classNames("savvy-ui-ml-3 savvy-ui-text-black-100 savvy-ui-text-left savvy-ui-leading-tight", className);
    return typeof children === "string" ? (React.createElement("span", __assign({ className: labelClasses, dangerouslySetInnerHTML: { __html: children } }, rest))) : (React.createElement("span", __assign({ className: labelClasses }, rest), children));
}
var WrappedRadio = React.forwardRef(function Radio(_a, ref) {
    var className = _a.className, label = _a.label, labelClassName = _a.labelClassName, wrapperClassName = _a.wrapperClassName, _b = _a.variant, variant = _b === void 0 ? "classic" : _b, rest = __rest(_a, ["className", "label", "labelClassName", "wrapperClassName", "variant"]);
    return (React.createElement("div", { className: wrapperClassName },
        React.createElement("label", { htmlFor: rest.id, className: classNames("savvy-ui-flex savvy-ui-items-center", {
                "savvy-ui-min-h-[56px] savvy-ui-cursor-pointer savvy-ui-rounded-lg savvy-ui-border savvy-ui-border-gray-200 savvy-ui-px-4 savvy-ui-py-4": variant === "contained",
            }) },
            React.createElement("div", { className: "savvy-ui-relative savvy-ui-inline-flex" },
                React.createElement("input", __assign({ className: classNames("savvy-ui-peer savvy-ui-h-6 savvy-ui-w-6 savvy-ui-appearance-none savvy-ui-rounded-full savvy-ui-border savvy-ui-border-gray-200", className), ref: ref }, rest, { type: "radio" })),
                React.createElement("span", { className: "savvy-ui-pointer-events-none savvy-ui-invisible savvy-ui-absolute savvy-ui-inset-[5px] savvy-ui-rounded-full savvy-ui-bg-savvy peer-checked:savvy-ui-visible" })),
            label ? (React.createElement(RadioLabel, { htmlFor: rest.id, className: labelClassName }, label)) : null)));
});

var WrappedNumberFormatField$1 = React.forwardRef(function NumberFormatField(_a, ref) {
    var className = _a.className, style = _a.style, label = _a.label, labelClassName = _a.labelClassName, errorMessage = _a.errorMessage, name = _a.name, id = _a.id, inputPrefix = _a.inputPrefix, _b = _a.prefixSpacing, prefixSpacing = _b === void 0 ? 32 : _b, rest = __rest(_a, ["className", "style", "label", "labelClassName", "errorMessage", "name", "id", "inputPrefix", "prefixSpacing"]);
    return (React.createElement(TextFieldContainer, { className: className, style: style, label: label, labelClassName: labelClassName, errorMessage: errorMessage, "aria-invalid": rest["aria-invalid"], id: id || name },
        React.createElement("div", { className: "savvy-ui-relative" },
            React.createElement(PatternFormat, __assign({ className: getTextFieldInputClasses({
                    hasError: Boolean(rest["aria-invalid"]),
                }), getInputRef: ref, name: name, id: id || name, style: __assign({}, (Boolean(inputPrefix) && { paddingLeft: prefixSpacing })) }, rest)),
            inputPrefix ? (React.createElement(TextFieldPrefix, { style: { width: prefixSpacing } }, inputPrefix)) : null)));
});

var WrappedDateTextField = React.forwardRef(function DateTextField(_a, ref) {
    var _b = _a.mask, mask = _b === void 0 ? "_" : _b, _c = _a.format, format = _c === void 0 ? "##/##/####" : _c, _d = _a.placeholder, placeholder = _d === void 0 ? "mm/dd/yyyy" : _d, _e = _a.type, type = _e === void 0 ? "text" : _e, rest = __rest(_a, ["mask", "format", "placeholder", "type"]);
    return (React.createElement(WrappedNumberFormatField$1, __assign({ mask: mask, format: format, placeholder: placeholder, type: type, ref: ref }, rest)));
});

var defaultStateOptions = usaStates.map(function (state) { return ({
    value: state.abbreviation,
    label: state.abbreviation,
}); });
function Address(_a) {
    var streetAddress1 = _a.streetAddress1, streetAddress2 = _a.streetAddress2, city = _a.city, state = _a.state, zipCode = _a.zipCode, googlePlacesApiKey = _a.googlePlacesApiKey, onPlaceSelected = _a.onPlaceSelected, className = _a.className;
    var _b = state.options, options = _b === void 0 ? defaultStateOptions : _b, _c = state.label, stateLabel = _c === void 0 ? "State" : _c, stateProps = __rest(state, ["options", "label"]);
    var streetAddress1Ref = streetAddress1.ref, _d = streetAddress1.label, streetAddress1Label = _d === void 0 ? "Street Address" : _d, _e = streetAddress1.name, streetAddressName = _e === void 0 ? "streetAddress1" : _e, streetAddress1Props = __rest(streetAddress1, ["ref", "label", "name"]);
    var placesWidget = usePlacesWidget({
        apiKey: googlePlacesApiKey,
        onPlaceSelected: onPlaceSelected,
        options: {
            componentRestrictions: { country: ["us"] },
            types: ["address"],
        },
    });
    return (React.createElement("div", { className: classNames("savvy-ui-space-y-4", className) },
        React.createElement(WrappedTextField$1, __assign({ ref: function (el) {
                if (!el) {
                    return;
                }
                if (streetAddress1Ref) {
                    if (typeof streetAddress1Ref === "function") {
                        streetAddress1Ref(el);
                    }
                    else {
                        streetAddress1Ref.current = el;
                    }
                }
                placesWidget.ref.current = el;
            } }, streetAddress1Props, { label: streetAddress1Label, name: streetAddressName, autoComplete: "off", className: "focus-within:savvy-ui-border-savvy" })),
        streetAddress2 ? (React.createElement(WrappedTextField$1, __assign({}, streetAddress2, { label: streetAddress2.label || "Apt/Suite #", name: streetAddress2.name || "streetAddress2" }))) : null,
        React.createElement(WrappedTextField$1, __assign({}, city, { label: city.label || "City", name: city.name || "city" })),
        React.createElement("div", { className: "savvy-ui-grid savvy-ui-grid-cols-2 savvy-ui-gap-2" },
            React.createElement(Select, __assign({ options: options }, stateProps, { label: stateLabel })),
            React.createElement(WrappedTextField$1, __assign({}, zipCode, { label: zipCode.label || "Zip Code", name: zipCode.name || "zipCode" })))));
}

var addressFields = {
    street_number: "short_name",
    route: "long_name",
    locality: "long_name",
    sublocality: "long_name",
    neighborhood: "long_name",
    administrative_area_level_1: "short_name",
    postal_code: "short_name",
};
var addressFieldKeys = Object.keys(addressFields);
function parseGooglePlacesAddress(placesAddress) {
    var _a;
    return (_a = placesAddress.address_components) === null || _a === void 0 ? void 0 : _a.reduce(function (address, field) {
        var _a;
        var addressType = field.types.find(function (type) { return addressFieldKeys.includes(type); }) ||
            field.types[0];
        var addressField = addressFields[addressType];
        return addressField
            ? __assign(__assign({}, address), (_a = {}, _a[addressType] = field[addressField], _a)) : address;
    }, {
        street_number: "",
        route: "",
        locality: "",
        sublocality: "",
        neighborhood: "",
        administrative_area_level_1: "",
        postal_code: "",
    });
}

var WrappedPhoneTextField = React.forwardRef(function PhoneTextField(_a, ref) {
    var _b = _a.mask, mask = _b === void 0 ? "" : _b, _c = _a.format, format = _c === void 0 ? "(###) ###-####" : _c, _d = _a.placeholder, placeholder = _d === void 0 ? "(###) ###-####" : _d, _e = _a.type, type = _e === void 0 ? "tel" : _e, rest = __rest(_a, ["mask", "format", "placeholder", "type"]);
    return (React.createElement(WrappedNumberFormatField$1, __assign({ mask: mask, format: format, placeholder: placeholder, type: type, ref: ref }, rest)));
});

function Arrow(_a) {
    var className = _a.className;
    return (React.createElement("div", { className: classNames("savvy-ui-border-gray-250 savvy-ui-z-10 savvy-ui-flex savvy-ui-h-8 savvy-ui-w-8 savvy-ui-items-center savvy-ui-justify-center savvy-ui-rounded-full savvy-ui-border savvy-ui-bg-white", className) },
        React.createElement("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
            React.createElement("path", { d: "M5 12H19", stroke: "#999999", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }),
            React.createElement("path", { d: "M12 5L19 12L12 19", stroke: "#999999", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }))));
}

function BundlePriceCompareLineItem(_a) {
    var title = _a.title, price = _a.price, _b = _a.pricePer, pricePer = _b === void 0 ? "mo" : _b, logo = _a.logo, estimate = _a.estimate;
    return (React.createElement("div", { className: "savvy-ui-flex savvy-ui-items-center savvy-ui-justify-between" },
        React.createElement("div", { className: "savvy-ui-flex savvy-ui-items-center" },
            React.createElement("div", { className: "savvy-ui-mr-1 savvy-ui-w-4" }, typeof logo === "string" ? React.createElement("img", { src: logo, alt: "" }) : logo),
            React.createElement("p", { className: "savvy-ui-text-xs savvy-ui-leading-none savvy-ui-text-black-300" }, title)),
        React.createElement("div", null,
            React.createElement("p", { className: "savvy-ui-flex savvy-ui-items-center" },
                React.createElement("span", { className: "savvy-ui-text-xs savvy-ui-font-bold savvy-ui-leading-[1.2]" }, "$"),
                React.createElement("span", { className: "savvy-ui-text-sm savvy-ui-font-bold savvy-ui-leading-[1.2]" }, Math.round(price)),
                React.createElement("span", { className: "savvy-ui-text-xs savvy-ui-leading-[1.2] savvy-ui-text-black-300" },
                    "/",
                    pricePer,
                    estimate ? "*" : "")))));
}
function BundlePriceCompareItem(_a) {
    var title = _a.title, _b = _a.totalPricePer, totalPricePer = _b === void 0 ? "mo" : _b, auto = _a.auto, home = _a.home, cheaper = _a.cheaper;
    var totalPrice = auto.price + home.price;
    return (React.createElement("div", { className: "savvy-ui-shrink-0 savvy-ui-grow savvy-ui-basis-0" },
        React.createElement("div", { className: "savvy-ui-p-4 savvy-ui-text-center" },
            React.createElement("p", { className: "savvy-ui-text-xs savvy-ui-leading-none savvy-ui-text-black-300" }, title),
            React.createElement("p", { className: "savvy-ui-flex savvy-ui-items-center savvy-ui-justify-center" },
                React.createElement("span", { className: classNames("savvy-ui-text-xs savvy-ui-font-bold savvy-ui-leading-[1.2]", { "savvy-ui-text-green": cheaper }) }, "$"),
                React.createElement("span", { className: classNames("savvy-ui-text-2xl savvy-ui-font-bold savvy-ui-leading-[1.2]", { "savvy-ui-text-green": cheaper }) }, Math.floor(totalPrice)),
                React.createElement("span", { className: "savvy-ui-text-xs savvy-ui-leading-[1.2] savvy-ui-text-black-300" },
                    "/",
                    totalPricePer,
                    auto.estimate || home.estimate ? "*" : ""))),
        React.createElement("div", { className: "savvy-ui-border-gray-250 savvy-ui-space-y-2 savvy-ui-border-t savvy-ui-bg-white-200 savvy-ui-py-2 savvy-ui-px-4" },
            React.createElement(BundlePriceCompareLineItem, { title: "Auto", price: auto.price, pricePer: auto.pricePer, logo: auto.logo, estimate: auto.estimate }),
            React.createElement(BundlePriceCompareLineItem, { title: "Home", price: home.price, pricePer: home.pricePer, logo: home.logo, estimate: home.estimate }))));
}
function BundlePriceCompare(_a) {
    var current = _a.current, selected = _a.selected, className = _a.className;
    var isEstimate = selected.auto.estimate || selected.home.estimate;
    var isBundleCheaper = current &&
        selected.auto.price + selected.home.price <
            current.auto.price + current.home.price;
    return (React.createElement("div", { className: classNames("savvy-ui-relative savvy-ui-mx-auto savvy-ui-w-full savvy-ui-min-w-[240px] savvy-ui-max-w-[343px]", { "savvy-ui-w-[240px]": !current }, className) },
        React.createElement("div", { className: "savvy-ui-divide-gray-250 savvy-ui-border-gray-250 savvy-ui-flex savvy-ui-divide-x savvy-ui-overflow-hidden savvy-ui-rounded-2xl savvy-ui-border savvy-ui-bg-white" },
            current ? (React.createElement(BundlePriceCompareItem, { title: "Current Offer", totalPricePer: current.pricePer, auto: current.auto, home: current.home })) : null,
            React.createElement(BundlePriceCompareItem, { title: "Selected Offer", totalPricePer: selected.pricePer, auto: selected.auto, home: selected.home, cheaper: isBundleCheaper })),
        current ? (React.createElement(Arrow, { className: "savvy-ui-absolute savvy-ui-left-1/2 savvy-ui-top-[22px] -savvy-ui-translate-x-1/2" })) : null,
        isEstimate ? (React.createElement("div", { className: "savvy-ui-mt-2 savvy-ui-text-center savvy-ui-text-sm savvy-ui-text-gray-100" },
            "*Savings and premiums are estimates",
            React.createElement("br", null),
            "and subject to change.")) : null));
}

function PolicyPremiumBlock(_a) {
    var title = _a.title, condition = _a.condition, price = _a.price, _b = _a.pricePer, pricePer = _b === void 0 ? "mo" : _b, logo = _a.logo, actionText = _a.actionText, className = _a.className;
    return (React.createElement("div", { className: classNames("savvy-ui-flex savvy-ui-flex-col savvy-ui-items-center savvy-ui-justify-center", className) },
        React.createElement("div", { className: "savvy-ui-w-full savvy-ui-overflow-hidden" },
            React.createElement("p", { className: "savvy-ui-mb-1 savvy-ui-truncate savvy-ui-text-center savvy-ui-text-xs savvy-ui-leading-none savvy-ui-text-black-300" }, title)),
        React.createElement("div", { className: "savvy-ui-flex savvy-ui-w-full savvy-ui-items-center savvy-ui-justify-center" },
            React.createElement("div", { className: "savvy-ui-w-9 savvy-ui-shrink-0" }, typeof logo === "string" ? React.createElement("img", { src: logo, alt: "" }) : logo),
            price || actionText ? (React.createElement("div", { className: "savvy-ui-relative savvy-ui-ml-1 savvy-ui-flex savvy-ui-items-center" }, price ? (React.createElement(React.Fragment, null,
                React.createElement("span", { className: "price-prefix savvy-ui-text-xs savvy-ui-font-bold" }, "$"),
                React.createElement("span", { className: "savvy-ui-text-2xl savvy-ui-font-bold" }, price),
                React.createElement("span", { className: "savvy-ui-text-xs savvy-ui-text-black-300" },
                    "/",
                    pricePer),
                condition ? (React.createElement("span", { className: "savvy-ui-absolute savvy-ui-left-0 savvy-ui-top-full -savvy-ui-mt-[2px] savvy-ui-whitespace-nowrap savvy-ui-text-xs savvy-ui-leading-none savvy-ui-text-black-300" }, condition)) : null)) : actionText ? (typeof actionText === "string" ? (React.createElement("span", { className: "savvy-ui-text-xs savvy-ui-font-bold", dangerouslySetInnerHTML: { __html: actionText } })) : (actionText)) : null)) : null)));
}

function SinglePriceCompare(_a) {
    var current = _a.current, selected = _a.selected, className = _a.className, _b = _a.divider, divider = _b === void 0 ? true : _b;
    return (React.createElement("div", { className: classNames("savvy-ui-border-250 savvy-ui-relative savvy-ui-max-w-[343px] savvy-ui-rounded-lg savvy-ui-border savvy-ui-bg-white", className) },
        React.createElement("div", { className: classNames("savvy-ui-flex savvy-ui-h-[88px]", {
                "savvy-ui-divide-gray-gray-250 savvy-ui-divide-x": divider,
            }) },
            current ? (React.createElement(PolicyPremiumBlock, __assign({}, current, { title: current.title || "Current Insurance", className: classNames("min-[390px]:savvy-ui-px-6 savvy-ui-shrink-0 savvy-ui-grow savvy-ui-basis-0 savvy-ui-px-4", { "savvy-ui-w-1/2": selected }) }))) : null,
            selected ? (React.createElement(PolicyPremiumBlock, __assign({}, selected, { className: classNames("min-[390px]:savvy-ui-px-6 savvy-ui-shrink-0 savvy-ui-grow savvy-ui-basis-0 savvy-ui-px-4", { "savvy-ui-w-1/2": current }) }))) : null),
        current && selected ? (React.createElement(Arrow, { className: classNames("savvy-ui-absolute savvy-ui-left-1/2 savvy-ui-top-1/2 -savvy-ui-translate-x-1/2 -savvy-ui-translate-y-1/2", {
                "savvy-ui-border-none": !divider,
            }) })) : null));
}

var WrappedAnchorButton = React.forwardRef(function AnchorButton(_a, ref) {
    var className = _a.className, _b = _a.size, size = _b === void 0 ? "default" : _b, _c = _a.variant, variant = _c === void 0 ? "filled" : _c, rest = __rest(_a, ["className", "size", "variant"]);
    return (React.createElement("a", __assign({ ref: ref, className: classNames(getButtonClassNames({
            size: size,
            variant: variant,
            disabled: Boolean(rest["aria-disabled"]),
        }), className) }, rest)));
});

var WrappedNumberFormatField = React.forwardRef(function NumberFormatField(_a, ref) {
    var className = _a.className, style = _a.style, label = _a.label, labelClassName = _a.labelClassName, errorMessage = _a.errorMessage, name = _a.name, id = _a.id, inputPrefix = _a.inputPrefix, _b = _a.prefixSpacing, prefixSpacing = _b === void 0 ? 32 : _b, rest = __rest(_a, ["className", "style", "label", "labelClassName", "errorMessage", "name", "id", "inputPrefix", "prefixSpacing"]);
    return (React.createElement(TextFieldContainer, { className: className, style: style, label: label, labelClassName: labelClassName, errorMessage: errorMessage, "aria-invalid": rest["aria-invalid"], id: id || name },
        React.createElement("div", { className: "savvy-ui-relative" },
            React.createElement(NumericFormat, __assign({ className: getTextFieldInputClasses({
                    hasError: Boolean(rest["aria-invalid"]),
                }), getInputRef: ref, name: name, id: id || name, style: __assign({}, (Boolean(inputPrefix) && { paddingLeft: prefixSpacing })) }, rest)),
            inputPrefix ? (React.createElement(TextFieldPrefix, { style: { width: prefixSpacing } }, inputPrefix)) : null)));
});

function Bulletin(_a) {
    var title = _a.title, text = _a.text, image = _a.image, className = _a.className;
    return (React.createElement("div", { className: classNames("savvy-ui-rounded savvy-ui-border savvy-ui-border-dashed savvy-ui-border-gray-200 savvy-ui-bg-white-200 savvy-ui-p-4", className) },
        React.createElement("div", { className: "savvy-ui-flex savvy-ui-items-center savvy-ui-text-sm" },
            React.createElement("div", { className: "savvy-ui-flex savvy-ui-grow savvy-ui-flex-col" },
                title ? (React.createElement("p", { className: "savvy-ui-mb-[2px] savvy-ui-font-bold" }, title)) : null,
                React.createElement("p", { className: "savvy-ui-text-black-300" }, text)),
            React.createElement("div", { className: "savvy-ui-ml-2 savvy-ui-w-8 savvy-ui-flex-none" }, typeof image === "string" ? React.createElement("img", { src: image, alt: "" }) : image))));
}

var Autocomplete = React.forwardRef(function Autocomplete(_a, ref) {
    var options = _a.options, value = _a.value, onChange = _a.onChange, placeholder = _a.placeholder, label = _a.label, errorMessage = _a.errorMessage, onInput = _a.onInput, rest = __rest(_a, ["options", "value", "onChange", "placeholder", "label", "errorMessage", "onInput"]);
    var _b = React.useState(""), query = _b[0], setQuery = _b[1];
    var openOnFocus = React.useRef(true);
    var isOpen = React.useRef(false);
    var buttonRef = React.useRef(null);
    var _c = React.useState(value), internalValue = _c[0], setInternvalValue = _c[1];
    var invalid = Boolean(rest["aria-invalid"]);
    var filteredOptions = query
        ? options.filter(function (option) {
            return option.label.toLowerCase().includes(query.toLowerCase()) ||
                option.value.toLowerCase().includes(query.toLowerCase());
        })
        : options;
    function handleChange(nextValue) {
        if (nextValue) {
            openOnFocus.current = false;
            setInternvalValue(nextValue);
            onChange(nextValue);
        }
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Combobox, { value: value, onChange: handleChange, as: "div", className: "savvy-ui-group savvy-ui-relative", by: "value" }, function (_a) {
            var open = _a.open;
            isOpen.current = open;
            return (React.createElement(React.Fragment, null,
                label ? (React.createElement(Combobox.Label, { className: classNames("savvy-ui-z-20", labelClasses, {
                        "savvy-ui-text-gray-100 group-focus-within:savvy-ui-text-savvy": !open && !rest["aria-invalid"],
                        "savvy-ui-text-savvy": open,
                        "savvy-ui-text-red group-focus-within:savvy-ui-text-red": rest["aria-invalid"],
                    }) }, label)) : null,
                React.createElement("div", { className: "savvy-ui-relative" },
                    React.createElement(Combobox.Input, __assign({ className: getTextFieldInputClasses({
                            hasError: invalid,
                            className: classNames("savvy-ui-w-full", {
                                "savvy-ui-rounded-b-none": open,
                            }),
                        }), onChange: function (ev) { return setQuery(ev.target.value); }, placeholder: value ? value.label : placeholder, displayValue: function (val) { var _a; return !internalValue ? "" : (_a = val === null || val === void 0 ? void 0 : val.label) !== null && _a !== void 0 ? _a : ""; }, onClick: function () {
                            var _a;
                            if (!isOpen.current) {
                                (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.click();
                            }
                        }, onFocus: function (ev) {
                            var _a, _b, _c;
                            if ((_b = (_a = ev.relatedTarget) === null || _a === void 0 ? void 0 : _a.id) === null || _b === void 0 ? void 0 : _b.includes("headlessui-combobox-button")) {
                                return;
                            }
                            if (open || openOnFocus.current === false) {
                                openOnFocus.current = true;
                                return;
                            }
                            setInternvalValue(undefined);
                            (_c = buttonRef.current) === null || _c === void 0 ? void 0 : _c.click();
                        }, onBlur: function () {
                            if (!internalValue) {
                                setInternvalValue(value);
                            }
                        }, ref: ref }, (onInput ? { onInput: onInput } : {}), rest)),
                    React.createElement(Combobox.Button, { className: classNames("savvy-ui-absolute savvy-ui-right-4 savvy-ui-top-4", {
                            "savvy-ui-rotate-180": open,
                        }), ref: buttonRef },
                        React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                            React.createElement("path", { d: "M0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8Z", fill: "#CACACA" }),
                            React.createElement("rect", { x: "5.17139", y: "5.16772", width: "6.01095", height: "2", transform: "rotate(45 5.17139 5.16772)", fill: "white" }),
                            React.createElement("rect", { x: "6.63916", y: "9.35913", width: "5.9248", height: "2", transform: "rotate(-45 6.63916 9.35913)", fill: "white" })))),
                React.createElement(Transition, { as: React.Fragment, leave: "transition ease-in duration-100", leaveFrom: "opacity-100", leaveTo: "opacity-0", unmount: true, beforeEnter: function () {
                        if (internalValue) {
                            setInternvalValue(undefined);
                        }
                    }, afterLeave: function () {
                        setQuery("");
                        if (!internalValue) {
                            setInternvalValue(value);
                        }
                    } },
                    React.createElement(Combobox.Options, { className: getDropdownClasses({
                            invalid: invalid,
                        }) }, filteredOptions.map(function (option, index) { return (React.createElement(Combobox.Option, { key: option.value + index, value: option, as: React.Fragment }, function (_a) {
                        var active = _a.active, selected = _a.selected;
                        return (React.createElement("li", { className: getDropdownOptionClasses({ active: active }) },
                            React.createElement("span", { className: "savvy-ui-truncate" }, option.label),
                            selected ? (React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "currentColor", xmlns: "http://www.w3.org/2000/svg", className: "savvy-ui-absolute savvy-ui-right-3 savvy-ui-top-1/2 -savvy-ui-translate-y-1/2 savvy-ui-text-savvy" },
                                React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M14.7045 2.15347C15.034 2.4045 15.0976 2.87509 14.8465 3.20457L6.84656 13.7045C6.71539 13.8767 6.51626 13.9838 6.30032 13.9983C6.08438 14.0128 5.87269 13.9334 5.71966 13.7803L1.21967 9.28034C0.926777 8.98745 0.926777 8.51258 1.21967 8.21969C1.51256 7.9268 1.98743 7.9268 2.28033 8.21969L6.1735 12.1129L13.6534 2.29551C13.9044 1.96603 14.375 1.90244 14.7045 2.15347Z", fill: "#3572E2" }))) : null));
                    })); })))));
        }),
        rest["aria-invalid"] && errorMessage ? (React.createElement("p", { className: "savvy-ui-mt-1 savvy-ui-text-xs savvy-ui-font-bold savvy-ui-text-red" }, errorMessage)) : null));
});

function DateFieldSet(props) {
    var errorMessage = props.errorMessage, id = props.id;
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "savvy-ui-grid savvy-ui-grid-cols-4 savvy-ui-gap-4" },
            React.createElement(WrappedNumberFormatField$1, __assign({ className: "col-span-1", mask: "", placeholder: "MM", label: "Month", id: "".concat(id, "-month") }, props.monthFieldProps, { format: "##" })),
            React.createElement(WrappedNumberFormatField$1, __assign({ className: "col-span-1", mask: "", placeholder: "DD", label: "Day", id: "".concat(id, "-day") }, props.dayFieldProps, { format: "##" })),
            React.createElement(WrappedNumberFormatField$1, __assign({ className: "col-span-2", mask: "", placeholder: "YYYY", label: "Year", id: "".concat(id, "-year") }, props.yearFieldProps, { format: "####" }))),
        errorMessage ? (React.createElement("p", { className: "savvy-ui-mt-1 savvy-ui-text-xs savvy-ui-font-bold savvy-ui-text-red" }, errorMessage)) : null));
}

var WrappedTextField = React.forwardRef(function TextAreaField(_a, ref) {
    var className = _a.className, style = _a.style, label = _a.label, labelClassName = _a.labelClassName, name = _a.name, id = _a.id, wrapperClassName = _a.wrapperClassName, wrapperStyle = _a.wrapperStyle, errorMessage = _a.errorMessage, rest = __rest(_a, ["className", "style", "label", "labelClassName", "name", "id", "wrapperClassName", "wrapperStyle", "errorMessage"]);
    return (React.createElement(TextFieldContainer, { className: wrapperClassName, label: label, labelClassName: labelClassName, id: id || name, style: wrapperStyle, "aria-invalid": rest["aria-invalid"], errorMessage: errorMessage },
        React.createElement("div", { className: "savvy-ui-relative" },
            React.createElement("textarea", __assign({ className: getTextFieldInputClasses({
                    className: className,
                    hasError: Boolean(rest["aria-invalid"]),
                }), ref: ref, name: name, id: id || name, style: style }, rest)))));
});

export { Address, WrappedAnchorButton as AnchorButton, Autocomplete, Bulletin, BundlePriceCompare, WrappedButton as Button, WrappedCheckbox as Checkbox, DateFieldSet, WrappedDateTextField as DateTextField, WrappedNumberFormatField as NumberFormatField, WrappedPhoneTextField as PhoneTextField, PolicyPremiumBlock, WrappedRadio as Radio, Select, SinglePriceCompare, WrappedTextField as TextAreaField, WrappedTextField$1 as TextField, parseGooglePlacesAddress };
//# sourceMappingURL=savvy-ui.esm.js.map
